import { ReactNode, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { useEnvStore } from 'store/EnvStore';
import { useResourceStore } from 'store/ResourceStore';

const HEARTBEAT_KEY = 'last_heartbeat';
const RECENT_THRESHOLD_MS = 7000;

type HeartbeatManagerProps = {
    children: ReactNode;
};

export default function HeartbeatManager({ children }: HeartbeatManagerProps) {
    const [status, setStatus] = useState<'checking' | 'blocked' | 'allowed'>('checking');
    const backendUrl = useEnvStore((s) => s.backendUrl);
    const projectId = useResourceStore((s) => s.projectId);

    useEffect(() => {
        const lastHeartbeat = localStorage.getItem(HEARTBEAT_KEY);
        if (!lastHeartbeat) {
            setStatus('allowed');
            return;
        }

        const lastHeartbeatMs = Number(lastHeartbeat);
        if (Number.isNaN(lastHeartbeatMs)) {
            setStatus('allowed');
            return;
        }

        const age = Date.now() - lastHeartbeatMs;
        if (age <= RECENT_THRESHOLD_MS) {
            setStatus('blocked');
        } else {
            setStatus('allowed');
        }
    }, []);

    useEffect(() => {
        const clearHeartbeat = () => {
            localStorage.removeItem(HEARTBEAT_KEY);
        };

        window.addEventListener('beforeunload', clearHeartbeat);
        window.addEventListener('pagehide', clearHeartbeat);

        return () => {
            window.removeEventListener('beforeunload', clearHeartbeat);
            window.removeEventListener('pagehide', clearHeartbeat);
            clearHeartbeat();
        };
    }, []);

    useEffect(() => {
        if (status !== 'allowed') {
            return;
        }

        const writeHeartbeat = () => {
            localStorage.setItem(HEARTBEAT_KEY, Date.now().toString());
        };

        writeHeartbeat();
        const intervalId = window.setInterval(writeHeartbeat, 5000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [status]);

    if (status === 'checking') {
        return null;
    }

    const handleContinue = () => {
        setStatus('allowed');
    };

    const modal = (
        <Dialog
            open
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent>
                <Stack spacing={1} alignItems="center">
                    <Typography variant="h6" component="p" align="center">
                        The 3D visualizer is already open in another tab.
                    </Typography>
                    <Typography variant="body1" align="center" color="text.secondary">
                        Multiple tabs are not supported and may lead to crashes or misbehaviours.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" width="100%">
                    <Button variant="outlined" size="large" onClick={() => window.location.replace(`${backendUrl}/projects/${projectId}`)}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" size="large" onClick={handleContinue}>
                        Continue
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );

    if (status === 'blocked') {
        return modal;
    }

    return <>{children}</>;
}
